import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { 
  Calendar as CalendarIcon, 
  Clock, 
  User, 
  Phone, 
  Mail, 
  Car,
  Edit,
  X,
  CheckCircle,
  AlertCircle,
  Search,
  Plus,
  Download,
  Upload,
  Printer
} from 'lucide-react';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import CreateBookingModal from './CreateBookingModal';
import EditBookingModal from './EditBookingModal';
import BulkImportModal from './BulkImportModal';
import { printBookingWorkCard, printDaySheet } from '../lib/printBookings';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const AdminBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bookingToEdit, setBookingToEdit] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isBulkImportModalOpen, setIsBulkImportModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [exporting, setExporting] = useState(false);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [printDate, setPrintDate] = useState(() => new Date().toISOString().split('T')[0]);
  const { toast } = useToast();
  const { token } = useAuth();

  useEffect(() => {
    fetchData();
    // Auto-refresh every minute
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, [statusFilter]);

  useEffect(() => {
    axios.get(`${API}/company-info`)
      .then(res => setCompanyInfo(res.data))
      .catch(() => {});
  }, []);

  const fetchData = async () => {
    try {
      const params = statusFilter !== 'all' ? { status: statusFilter } : {};
      const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
      const [bookingsRes, statsRes] = await Promise.all([
        axios.get(`${API}/bookings`, { params, ...authHeaders }),
        axios.get(`${API}/bookings/stats`, authHeaders)
      ]);
      setBookings(bookingsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditBooking = (booking) => {
    setSelectedBooking(booking);
    setIsEditModalOpen(true);
  };

  const handleUpdateStatus = async (bookingId, newStatus) => {
    try {
      await axios.put(`${API}/bookings/${bookingId}`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({
        title: "Status Oppdatert",
        description: `Booking status endret til ${newStatus}`,
      });
      fetchData();
    } catch (error) {
      toast({
        title: "Feil",
        description: "Kunne ikke oppdatere status",
        variant: "destructive"
      });
    }
  };

  const handleCancelBooking = async (bookingId) => {
    if (!window.confirm('Er du sikker på at du vil kansellere denne bookingen?')) {
      return;
    }

    try {
      await axios.delete(`${API}/bookings/${bookingId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({
        title: "Booking Kansellert",
        description: "Bookingen er kansellert",
      });
      fetchData();
    } catch (error) {
      toast({
        title: "Feil",
        description: "Kunne ikke kansellere booking",
        variant: "destructive"
      });
    }
  };

  const handleContactCustomer = (booking) => {
    const subject = `Angående din booking ${booking.booking_number}`;
    const body = `Hei ${booking.customer_name},\n\nAngående din booking:\nDato: ${booking.date}\nTidspunkt: ${booking.time_slot}\n\n`;
    window.location.href = `mailto:${booking.customer_email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/bookings/export/csv`, {
        params: { status: statusFilter },
        headers: {
          Authorization: `Bearer ${token}`
        },
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `bookinger_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast({
        title: "Eksport Fullført! ✅",
        description: "Bookinger er eksportert til CSV",
      });
    } catch (error) {
      console.error('Error exporting CSV:', error);
      toast({
        title: "Feil",
        description: "Kunne ikke eksportere bookinger",
        variant: "destructive"
      });
    } finally {
      setExporting(false);
    }
  };

  const handlePrintDay = async () => {
    try {
      const res = await axios.get(`${API}/bookings`, {
        params: { date: printDate },
        headers: { Authorization: `Bearer ${token}` }
      });
      const dayBookings = (res.data || []).filter(b => b.status !== 'cancelled');
      const label = new Date(printDate).toLocaleDateString('no-NO', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
      printDaySheet(label, dayBookings, companyInfo);
    } catch (error) {
      toast({ title: "Feil", description: "Kunne ikke hente bookinger for dagen", variant: "destructive" });
    }
  };

  const filteredBookings = bookings.filter(booking => {
    const searchLower = searchTerm.toLowerCase();
    return (
      booking.customer_name.toLowerCase().includes(searchLower) ||
      booking.customer_phone.includes(searchTerm) ||
      booking.booking_number.toLowerCase().includes(searchLower) ||
      booking.customer_email.toLowerCase().includes(searchLower)
    );
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
        return 'bg-blue-600/20 text-blue-400 border-blue-600/50';
      case 'completed':
        return 'bg-green-600/20 text-green-400 border-green-600/50';
      case 'cancelled':
        return 'bg-red-600/20 text-red-400 border-red-600/50';
      default:
        return 'bg-gray-600/20 text-gray-400 border-gray-600/50';
    }
  };

  if (loading || !stats) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-white text-xl">Laster bookinger...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Create Button */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-white mb-2">
            <span className="bg-gradient-to-r from-red-500 to-yellow-400 bg-clip-text text-transparent">
              Booking Oversikt
            </span>
          </h2>
          <p className="text-gray-400">Administrer alle bookinger</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 bg-gray-900/60 border border-gray-700 rounded-lg px-2 py-1">
            <Input
              type="date"
              value={printDate}
              onChange={(e) => setPrintDate(e.target.value)}
              data-testid="print-day-date-input"
              className="w-40 bg-transparent border-0 text-white h-8 focus-visible:ring-0"
            />
            <Button
              onClick={handlePrintDay}
              variant="outline"
              size="sm"
              data-testid="print-day-btn"
              className="border-purple-600 text-purple-400 hover:bg-purple-600/20"
            >
              <Printer className="w-4 h-4 mr-2" />
              Skriv ut dag
            </Button>
          </div>
          <Button
            onClick={() => setIsBulkImportModalOpen(true)}
            variant="outline"
            className="border-blue-600 text-blue-400 hover:bg-blue-600/20"
          >
            <Upload className="w-4 h-4 mr-2" />
            Bulk Import
          </Button>
          <Button
            onClick={handleExportCSV}
            disabled={exporting}
            variant="outline"
            className="border-green-600 text-green-400 hover:bg-green-600/20"
          >
            {exporting ? (
              <>
                <span className="animate-spin mr-2">⏳</span>
                Eksporterer...
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Eksporter CSV
              </>
            )}
          </Button>
          <Button
            onClick={() => setIsCreateModalOpen(true)}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Ny Booking
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 border-gray-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-gray-400">Totalt</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-white">{stats.total}</div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-900/30 to-blue-800/30 border-blue-700/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-gray-400">Bekreftet</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-white">{stats.confirmed}</div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-900/30 to-green-800/30 border-green-700/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-gray-400">Fullført</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-white">{stats.completed}</div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-red-900/30 to-red-800/30 border-red-700/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-gray-400">Kansellert</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-white">{stats.cancelled}</div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-yellow-900/30 to-yellow-800/30 border-yellow-700/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-gray-400">I dag</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-white">{stats.today}</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white">Bookinger</CardTitle>
          <CardDescription className="text-gray-400">Administrer alle bookinger</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-3 w-5 h-5 text-gray-500" />
              <Input
                placeholder="Søk etter navn, telefon, email eller booking nummer..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-gray-900 border-gray-700 text-white"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-48 bg-gray-900 border-gray-700 text-white">
                <SelectValue placeholder="Filtrer status" />
              </SelectTrigger>
              <SelectContent className="bg-gray-900 border-gray-700 text-white">
                <SelectItem value="all">Alle</SelectItem>
                <SelectItem value="confirmed">Bekreftet</SelectItem>
                <SelectItem value="completed">Fullført</SelectItem>
                <SelectItem value="cancelled">Kansellert</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Bookings List */}
          <div className="space-y-3 max-h-[600px] overflow-y-auto">
            {filteredBookings.length > 0 ? (
              filteredBookings.map((booking) => (
                <div
                  key={booking.id}
                  className="bg-gray-900/50 border border-gray-700 rounded-xl p-4 hover:border-red-600/50 transition-all duration-300"
                >
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-bold text-white">{booking.customer_name}</h3>
                        <Badge className={getStatusColor(booking.status)}>
                          {booking.status}
                        </Badge>
                        <span className="text-sm text-gray-500">#{booking.booking_number}</span>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-400">
                        <div className="flex items-center space-x-2">
                          <CalendarIcon className="w-4 h-4" />
                          <span>{booking.date}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Clock className="w-4 h-4" />
                          <span>{booking.time_slot} - Plass {booking.slot_number}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Phone className="w-4 h-4" />
                          <a href={`tel:${booking.customer_phone}`} className="hover:text-red-400">
                            {booking.customer_phone}
                          </a>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Mail className="w-4 h-4" />
                          <a href={`mailto:${booking.customer_email}`} className="hover:text-red-400 truncate">
                            {booking.customer_email}
                          </a>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Car className="w-4 h-4" />
                          <span className="capitalize">{booking.vehicle_type}</span>
                        </div>
                      </div>

                      {booking.notes && (
                        <div className="mt-2 text-sm text-gray-500 italic">
                          Notat: {booking.notes}
                        </div>
                      )}
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-yellow-600 text-yellow-400 hover:bg-yellow-600/20"
                        onClick={() => {
                          setBookingToEdit(booking);
                          setIsEditModalOpen(true);
                        }}
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Rediger
                      </Button>

                      <Button
                        size="sm"
                        variant="outline"
                        data-testid={`print-booking-${booking.id}`}
                        className="border-purple-600 text-purple-400 hover:bg-purple-600/20"
                        onClick={() => printBookingWorkCard(booking, companyInfo)}
                      >
                        <Printer className="w-4 h-4 mr-1" />
                        Skriv ut
                      </Button>

                      <Button
                        size="sm"
                        variant="outline"
                        className="border-blue-600 text-blue-400 hover:bg-blue-600/20"
                        onClick={() => handleContactCustomer(booking)}
                      >
                        <Mail className="w-4 h-4 mr-1" />
                        Kontakt
                      </Button>

                      {booking.status === 'confirmed' && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-green-600 text-green-400 hover:bg-green-600/20"
                          onClick={() => handleUpdateStatus(booking.id, 'completed')}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Fullført
                        </Button>
                      )}

                      {booking.status !== 'cancelled' && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-red-600 text-red-400 hover:bg-red-600/20"
                          onClick={() => handleCancelBooking(booking.id)}
                        >
                          <X className="w-4 h-4 mr-1" />
                          Kanseller
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-12 text-gray-500">
                Ingen bookinger funnet
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Create Booking Modal */}
      <CreateBookingModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onBookingCreated={() => {
          fetchData(); // Refresh bookings list
        }}
      />

      {/* Edit Booking Modal */}
      <EditBookingModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setBookingToEdit(null);
        }}
        booking={bookingToEdit}
        onBookingUpdated={() => {
          fetchData(); // Refresh bookings list
        }}
      />

      {/* Bulk Import Modal */}
      <BulkImportModal
        isOpen={isBulkImportModalOpen}
        onClose={() => setIsBulkImportModalOpen(false)}
        onImportComplete={() => {
          fetchData(); // Refresh bookings list
        }}
      />
    </div>
  );
};

export default AdminBookings;
